import React from 'react';
import PropTypes from 'prop-types';
import { ProgressBar } from 'react-bootstrap';
import CircleProgress from '../Common/CircleProgress';
import {
  GradesScoreIcon,
  IterationsScoreIcon,
  PerformanceScoreIcon,
} from '../Common/Icons';
import './LevelProgress.sass';

const LevelProgressBar = ({ required, earned }) => {
  const progress = Math.min((earned / required) * 100, 100);
  let progressBarVariant = 'info';
  if (progress < 25) {
    progressBarVariant = 'warning';
  } else if (progress > 75) {
    progressBarVariant = 'success';
  }

  const label = `${earned} / ${required}`;

  const quotes = [
    '"When face to face with a difficulty, you are up against a discovery." - Lord Kelvin',
    '"If you want to increase your success rate, double your failure rate." - Thomas Watson Jr.',
    '"Develop success from failures. Discouragement and failure are two of the surest stepping stones to success." - Dale Carnegie',
    '"I have not failed, but found 1000 ways not to make a light bulb." - Thomas Edison',
    '"As engineers, we were going to be in a position to change the world - not just study it." - Henry Petroski',
    '"We are continually faced by great opportunities brilliantly disguised as insoluble problems." - Lee Iacocca',
  ];
  const message = quotes[Math.floor(Math.random() * quotes.length)];

  return (
    <>
      <div className="fs-5 mb-1">
        Your score: <b>{earned}</b> / {required} pts
      </div>
      <ProgressBar
        animated
        striped
        variant={progressBarVariant}
        now={progress}
        label={label}
        visuallyHidden
      />
      <span className="mt-1">
        <i>{message}</i>
      </span>
    </>
  );
};

const LevelProgress = ({ game }) => {
  const {
    requiredGradesScore,
    requiredIterationsScore,
    requiredPerformanceScore,
  } = game.level;
  const metMinGrades = game.gradesScore >= requiredGradesScore;
  const metMinIterations = game.iterationsScore >= requiredIterationsScore;
  const metMinPerformance = game.performanceScore >= requiredPerformanceScore;

  return (
    <div className="game-overview p-3 py-0">
      {!game.canLevelUp && (
        <div className="text-center mt-2 mb-3">
          <LevelProgressBar
            required={game.level.requiredAggregateScore}
            earned={game.aggregateScore}
          />
        </div>
      )}
      <div className="box box-row">
        <div className="box box-stretch p-2">
          <div className="p-3">
            <CircleProgress
              primary={game.gradesScore}
              secondary={requiredGradesScore}
              success={metMinGrades}
            >
              <GradesScoreIcon className="fs-1 mb-2" />
              <h4>
                <span className="fw-bold">{game.gradesScore}</span> points
              </h4>
              GRADES
            </CircleProgress>
          </div>

          <h5 className="text-center">Grades</h5>
          {!game.isAtLastLevel && requiredGradesScore > game.gradesScore && (
            <p className="game-component-req">
              You must earn at least{' '}
              <strong>{requiredGradesScore} points</strong> in this category to
              move to the next level.
            </p>
          )}
        </div>
        <div className="box box-stretch p-2">
          <div className="p-3">
            <CircleProgress
              primary={(game.iterationsScore / 50) * 100}
              secondary={(requiredIterationsScore / 50) * 100}
              success={metMinIterations}
            >
              <IterationsScoreIcon className="fs-1 mb-2" />
              <h4>
                <span className="fw-bold">{game.iterationsScore}</span> points
              </h4>
              ITERATIONS
            </CircleProgress>
          </div>

          <h5 className="text-center">Iterations</h5>
          {!game.isAtLastLevel &&
            requiredIterationsScore > game.iterationsScore && (
              <p className="game-component-req">
                You must earn at least{' '}
                <strong>{requiredIterationsScore} points</strong> in this
                category to move to the next level.
              </p>
            )}
        </div>
        <div className="box box-stretch p-2">
          <div className="p-3">
            <CircleProgress
              primary={game.performanceScore}
              secondary={requiredPerformanceScore}
              success={metMinPerformance}
            >
              <PerformanceScoreIcon className="fs-1 mb-2" />
              <h4>
                <span className="fw-bold">{game.performanceScore}</span> points
              </h4>
              PERFORMANCE
            </CircleProgress>
          </div>

          <h5 className="text-center">Performance</h5>
          {!game.isAtLastLevel &&
            requiredPerformanceScore > game.performanceScore && (
              <p className="game-component-req">
                You must earn at least{' '}
                <strong>{requiredPerformanceScore} points</strong> in this
                category to move to the next level.
              </p>
            )}
        </div>
      </div>
    </div>
  );
};

LevelProgress.propTypes = {
  game: PropTypes.object.isRequired,
};

export default LevelProgress;
